import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { 
  GET_DOCUMENT_CRITICAL_EVENTS, 
  MARK_CRITICAL_EVENTS_COMPLETED,
  MARK_CRITICAL_EVENTS_INCOMPLETE 
} from '../../api/queries/criticalEvents';
import { paths } from '../../routing';
import { useNotifications } from '../../notifications';
import { parseLocalDate } from '../../utils/date';
import { 
  getEventUrgency, 
  getRelativeTime,
  formatFinancialAmount 
} from './helpers/eventFormatters';
import { ReactComponent as DocumentIcon } from '../../shared/icons/document-icon.svg';
import { ReactComponent as DealIcon } from '../../shared/icons/deals-icon.svg';
import { ReactComponent as CalendarIcon } from '../../shared/icons/calendar.svg';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import styles from './EventDetailsSlideout.module.scss';

interface ActorContext {
  primary_actor?: string;
}

interface ActionContext {
  action_required?: string;
  notice_requirements?: string;
  consequences_if_missed?: string;
}

interface FinancialContext {
  base_rent?: number | string;
  deposit_amount?: number | string;
  square_feet?: number | string;
  penalty_amount?: number | string;
}

interface PartyContext {
  landlord?: string;
  tenant?: string;
  buyer?: string;
  seller?: string;
}

export interface CriticalEvent {
  id: string;
  eventType: string;
  eventDate: string;
  eventLabel: string;
  status: string;
  document: {
    id: string;
    title: string;
    status: string;
  };
  deal: {
    id: string;
    title: string;
  };
  actorContext: string | ActorContext | null;
  actionContext: string | ActionContext | null;
  financialContext: string | FinancialContext | null;
  partyContext: string | PartyContext | null;
  supportingBlurb: string;
  sourceSection: string;
  sourcePage: number;
  sourceSnippet: string;
  derivationRule: string;
}

interface EventDetailsSlideoutProps {
  event: CriticalEvent;
  isOpen: boolean;
  onClose: () => void;
  onNavigateToEvent: (eventId: string) => void;
  teamId: string;
}

const EventDetailsSlideout: React.FC<EventDetailsSlideoutProps> = ({
  event,
  isOpen,
  onClose,
  onNavigateToEvent,
  teamId,
}) => {
  const navigate = useNavigate();
  const [eventStatus, setEventStatus] = useState(event.status);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Update status when event changes
  useEffect(() => {
    setEventStatus(event.status);
  }, [event.id, event.status]);

  // Mark event as completed mutation
  const [markEventCompleted, { loading: markingCompleted }] = useMutation(MARK_CRITICAL_EVENTS_COMPLETED, {
    onCompleted: () => {
      setEventStatus('completed');
      notifySuccess({ message: 'Event marked as completed' });
    },
    onError: (error) => {
      notifyError({ message: `Failed to mark event as completed: ${error.message}` });
    },
  });

  // Mark event as incomplete mutation
  const [markEventIncomplete, { loading: markingIncomplete }] = useMutation(MARK_CRITICAL_EVENTS_INCOMPLETE, {
    onCompleted: () => {
      // Backend sets it back to 'active' status
      setEventStatus('active');
      notifySuccess({ message: 'Event marked as incomplete' });
    },
    onError: (error) => {
      notifyError({ message: `Failed to mark event as incomplete: ${error.message}` });
    },
  });

  // Handle mark completed
  const handleMarkCompleted = () => {
    markEventCompleted({
      variables: { eventIds: [event.id] },
    });
  };

  // Handle mark incomplete (set back to active status)
  const handleMarkIncomplete = () => {
    markEventIncomplete({
      variables: { eventIds: [event.id] },
    });
  };

  // Fetch related events from the same document
  const { data: relatedEventsData } = useQuery(GET_DOCUMENT_CRITICAL_EVENTS, {
    variables: { documentId: event.document.id },
    skip: !event.document.id,
  });

  // Parse context data
  const contextData = useMemo(() => {
    function parseContext<T>(context: string | T | null): T {
      if (!context) return {} as T;
      if (typeof context === 'string') {
        try {
          return JSON.parse(context);
        } catch {
          return {} as T;
        }
      }
      return context;
    }

    return {
      actor: parseContext<ActorContext>(event.actorContext),
      action: parseContext<ActionContext>(event.actionContext),
      financial: parseContext<FinancialContext>(event.financialContext),
      party: parseContext<PartyContext>(event.partyContext),
    };
  }, [event]);

  // Filter related events (exclude current)
  const relatedEvents = useMemo(() => {
    if (!relatedEventsData?.getCriticalEventsByDocument) return [];
    return relatedEventsData.getCriticalEventsByDocument
      .filter((e: CriticalEvent) => e.id !== event.id)
      .sort((a: CriticalEvent, b: CriticalEvent) => parseLocalDate(a.eventDate).valueOf() - parseLocalDate(b.eventDate).valueOf());
  }, [relatedEventsData, event.id]);

  const urgency = getEventUrgency(event.eventDate, event.status);

  // Don't render content if we don't have valid event data, but keep portal for animation
  const hasValidEvent = event && event.id && event.id !== '';

  // Always render the portal (like MobileSidebar) to enable smooth transitions
  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {isOpen && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div className={styles.slideout}>
          {hasValidEvent ? (
            <>
              {/* Sticky Header */}
              <div className={styles.slideout__header}>
                <div className={styles.slideout__headerContent}>
                  {/* Row 1: Event Title with close button */}
                  <div className={styles.slideout__titleRow}>
                    <div className={styles.slideout__title}>
                      {event.eventLabel}
                    </div>
                    <div className={styles.slideout__close} onClick={onClose}>
                      <CloseIcon />
                    </div>
                  </div>
              
              {/* Row 2: Date and Status */}
              <div className={styles.slideout__dateRow}>
                <CalendarIcon width={16} height={16} />
                <span className={styles.date}>{parseLocalDate(event.eventDate).format('MMMM D, YYYY')}</span>
                <span className={styles.relative}>({getRelativeTime(event.eventDate)})</span>
                <span className={`badge badge-pill ${
                  eventStatus === 'completed' 
                    ? styles.statusPillCompleted 
                    : (eventStatus === 'active' || eventStatus === 'overdue') && parseLocalDate(event.eventDate).isBefore(dayjs(), 'day')
                    ? styles.statusPillPast
                    : styles.statusPillUpcoming
                }`}>
                  {eventStatus === 'completed' 
                    ? 'Completed' 
                    : (eventStatus === 'active' || eventStatus === 'overdue') && parseLocalDate(event.eventDate).isBefore(dayjs(), 'day')
                    ? 'Past Due'
                    : 'Upcoming'}
                </span>
              </div>
              
              {/* Row 4: Action Buttons */}
              <div className={styles.slideout__buttonsRow}>
                <div className={styles.slideout__buttonsLeft}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      navigate(
                        paths.Documents.document
                          .replace(':teamId', teamId)
                          .replace(':dealId', event.deal.id)
                          .replace(':documentId', event.document.id)
                      );
                    }}
                  >
                    <DocumentIcon width={16} height={16} />
                    <span>View Document</span>
                  </Button>
                  
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      navigate(
                        paths.deals.detail
                          .replace(':teamId', teamId)
                          .replace(':dealId', event.deal.id)
                      );
                    }}
                  >
                    <DealIcon width={16} height={16} />
                    <span>View Deal</span>
                  </Button>
                </div>
                
                {eventStatus !== 'completed' ? (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleMarkCompleted}
                    disabled={markingCompleted}
                  >
                    <FormattedMessage id="tracker.mark-completed" defaultMessage="Mark Completed" />
                  </Button>
                ) : (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleMarkIncomplete}
                    disabled={markingIncomplete}
                  >
                    <FormattedMessage id="tracker.mark-incomplete" defaultMessage="Mark Incomplete" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Horizontal Divider */}
            <div className={styles.slideout__divider} />
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
        {/* Supporting Context */}
        {event.supportingBlurb && (
          <div className={classNames(styles.sectionCard, styles['sectionCard--summary'])}>
            <h3 className={styles.sectionTitle}>
              <FormattedMessage id="tracker.details.summary" defaultMessage="Summary" />
            </h3>
            <p className={styles.blurb}>{event.supportingBlurb}</p>
          </div>
        )}

        {/* Action Required */}
        {(contextData.actor.primary_actor || contextData.action.action_required) && (
          <div className={classNames(styles.sectionCard, styles['sectionCard--primary'])}>
            <h3 className={styles.sectionTitle}>
              <span style={{ marginRight: '0.5rem' }}>⚡</span>
              <FormattedMessage id="tracker.details.action-required" defaultMessage="Action Required" />
            </h3>
            {contextData.actor.primary_actor && (
              <div className={styles.actionItem}>
                <strong>Who:</strong> {contextData.actor.primary_actor}
              </div>
            )}
            {contextData.action.action_required && (
              <div className={styles.actionItem}>
                <strong>What:</strong> {contextData.action.action_required}
              </div>
            )}
            {contextData.action.notice_requirements && (
              <div className={styles.noticeBox}>
                <strong>Notice Requirements:</strong> {contextData.action.notice_requirements}
              </div>
            )}
            {contextData.action.consequences_if_missed && (
              <div className={styles.consequences}>
                <strong>If Missed:</strong> {contextData.action.consequences_if_missed}
              </div>
            )}
          </div>
        )}

        {/* Financial Details */}
        {(contextData.financial.base_rent || contextData.financial.deposit_amount || 
          contextData.financial.square_feet || contextData.financial.penalty_amount) && (
          <div className={classNames(styles.sectionCard, styles['sectionCard--financial'])}>
            <h3 className={styles.sectionTitle}>
              <span style={{ marginRight: '0.5rem' }}>💰</span>
              <FormattedMessage id="tracker.details.financial" defaultMessage="Financial Details" />
            </h3>
            <div className={styles.financialGrid}>
              {contextData.financial.base_rent && (
                <div className={styles.financialItem}>
                  <div className={styles.label}>Base Rent</div>
                  <div className={classNames(styles.value, styles['value--currency'])}>
                    {formatFinancialAmount(contextData.financial.base_rent)}
                  </div>
                </div>
              )}
              {contextData.financial.deposit_amount && (
                <div className={styles.financialItem}>
                  <div className={styles.label}>Deposit</div>
                  <div className={classNames(styles.value, styles['value--currency'])}>
                    {formatFinancialAmount(contextData.financial.deposit_amount)}
                  </div>
                </div>
              )}
              {contextData.financial.square_feet && (
                <div className={styles.financialItem}>
                  <div className={styles.label}>Square Feet</div>
                  <div className={styles.value}>
                    {Number(contextData.financial.square_feet).toLocaleString()} SF
                  </div>
                </div>
              )}
              {contextData.financial.penalty_amount && (
                <div className={styles.financialItem}>
                  <div className={styles.label}>Penalty</div>
                  <div className={classNames(styles.value, styles['value--currency'])}>
                    {formatFinancialAmount(contextData.financial.penalty_amount)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Party Information */}
        {Object.keys(contextData.party).length > 0 && (
          <div className={classNames(styles.sectionCard, styles['sectionCard--parties'])}>
            <h3 className={styles.sectionTitle}>
              <span style={{ marginRight: '0.5rem' }}>👥</span>
              <FormattedMessage id="tracker.details.parties" defaultMessage="Parties Involved" />
            </h3>
            <div className={styles.detailsGrid}>
              {contextData.party.landlord && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Landlord:</span>
                  <span className={styles.value}>{contextData.party.landlord}</span>
                </div>
              )}
              {contextData.party.tenant && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Tenant:</span>
                  <span className={styles.value}>{contextData.party.tenant}</span>
                </div>
              )}
              {contextData.party.buyer && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Buyer:</span>
                  <span className={styles.value}>{contextData.party.buyer}</span>
                </div>
              )}
              {contextData.party.seller && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Seller:</span>
                  <span className={styles.value}>{contextData.party.seller}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Source Information */}
        {(event.sourceSection || event.sourceSnippet) && (
          <div className={classNames(styles.sectionCard, styles['sectionCard--source'])}>
            <h3 className={styles.sectionTitle}>
              <span style={{ marginRight: '0.5rem' }}>📄</span>
              <FormattedMessage id="tracker.details.source" defaultMessage="Document Source" />
            </h3>
            {event.sourceSection && (
              <div className={styles.sourceItem}>
                <strong>{event.sourceSection}</strong>
                {event.sourcePage && <span> (Page {event.sourcePage})</span>}
              </div>
            )}
            {event.sourceSnippet && (
              <blockquote className={styles.snippet}>"{event.sourceSnippet}"</blockquote>
            )}
            {event.derivationRule && (
              <div className={styles.derivation}>
                <em>Calculated: {event.derivationRule}</em>
              </div>
            )}
          </div>
        )}

        {/* Related Events */}
        {relatedEvents.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FormattedMessage
                id="tracker.details.related-events"
                defaultMessage="Other Events in This Document"
              />
            </h3>
            <div className={styles.relatedEvents}>
              {relatedEvents.map((relatedEvent) => (
                <button
                  key={relatedEvent.id}
                  className={styles.relatedEvent}
                  onClick={() => onNavigateToEvent(relatedEvent.id)}
                >
                  <div className={styles.relatedDate}>
                    {parseLocalDate(relatedEvent.eventDate).format('MMM D, YYYY')}
                  </div>
                  <div className={styles.relatedLabel}>{relatedEvent.eventLabel}</div>
                </button>
              ))}
            </div>
          </div>
        )}
          </div>
            </>
          ) : null}
        </div>
      </div>
    </>,
    document.body
  );
};

export default EventDetailsSlideout;